import { safeParseFloat, isRenewableTechnology } from './CommonUtils';

// Define constants for property names and unit conversions
const REGION_GRID_PROPERTY = "Region | Grid";
const BUSINESS_SEGMENT_PROPERTY = "Business Segment";
const UNITS_PROPERTY = "Units";
const R4Q_PROPERTY = "R4Q";
const COUNTRY_PROPERTY = "Country";
const TECHNOLOGY_PROPERTY = "Technology";
const REGION_VOLUME_RENEWABLE = "renewable-volume";
const REGION_VOLUME_NONRENEWABLE = "non-renewable";
const REGION_VOLUME_RESIDUALGRID = "residual-grid";
const REGION_GRID_REGION = "grid-region";

// Unit constants
const TWH_UNIT = "TWh";
const GWH_UNIT = "GWh";
const MWH_UNIT = "MWh";

// Conversion factors
const GWH_TO_MWH = 1000;
const TWH_TO_MWH = 1000000;
const MWH_TO_GWH = 1;
const TWH_TO_GWH = 1000;
const MWH_TO_TWH = 1 / 1000000;
const GWH_TO_TWH = 1 / 1000;

// Business segment identifiers
const SE_PREFIX = "SE |";
const SET_IDENTIFIER = "SET";

// Type alias for grid region data values
type GridRegionDataValue = string | number | boolean | undefined;

// Interfaces for data structures
export interface GridRegionDataItem {
  [REGION_GRID_PROPERTY]?: string;
  [BUSINESS_SEGMENT_PROPERTY]?: string;
  [UNITS_PROPERTY]?: string;
  [TECHNOLOGY_PROPERTY]?: string;
  [COUNTRY_PROPERTY]?: string;
  [key: string]: GridRegionDataValue; // Use a specific type alias
}

export interface ProcessedGridRegionData {
  country: string;
  [REGION_GRID_REGION]: string;
  [R4Q_PROPERTY]: number;
  [REGION_VOLUME_RENEWABLE]: number;
  [REGION_VOLUME_NONRENEWABLE]: number;
  [REGION_VOLUME_RESIDUALGRID]: number;
  [key: string]: string | number; // More specific than any
}

interface RegionData {
  [REGION_GRID_REGION]: string;
  [R4Q_PROPERTY]: number;
  [REGION_VOLUME_RENEWABLE]: number;
  [REGION_VOLUME_NONRENEWABLE]: number;
  [REGION_VOLUME_RESIDUALGRID]: number;
}

export interface CountryData {
  totalRenewable: number;
  totalNonRenewable: number;
  totalResidualGrid: number;
  totalR4Q: number;
  regions: RegionData[];
}

export interface CountrySummaries {
  [country: string]: CountryData;
}

interface RawData {
  [sheetName: string]: Array<GridRegionDataItem>;
}

/**
 * Check if the business segment is SE-related (but not SET)
 * @param businessSegment - The business segment string
 * @returns True if it's an SE segment but not SET
 */
export function isSEBusinessSegment(businessSegment?: string): boolean {
  // Return true only if it starts with 'SE |' and doesn't contain 'SET'
  return !!businessSegment && 
         businessSegment.startsWith(SE_PREFIX) && 
         !businessSegment.includes(SET_IDENTIFIER);
}

/**
 * Convert energy values based on source and target units
 * @param value - The value to convert
 * @param sourceUnit - Source unit (GWh, MWh, TWh)
 * @param targetUnit - Target unit (GWh, MWh, TWh)
 * @returns Converted value
 */
export function convertEnergyValue(
  value: number, 
  sourceUnit: string, 
  targetUnit: string
): number {
  if (!value || isNaN(value)) {
    return 0;
  }
  
  // Convert to MWh
  if (sourceUnit === GWH_UNIT && targetUnit === MWH_UNIT) {
    return value * GWH_TO_MWH;
  } else if (sourceUnit === TWH_UNIT && targetUnit === MWH_UNIT) {
    return value * TWH_TO_MWH;
  }
  
  // Convert to GWh
  else if (sourceUnit === MWH_UNIT && targetUnit === GWH_UNIT) {
    return value * MWH_TO_GWH;
  } else if (sourceUnit === TWH_UNIT && targetUnit === GWH_UNIT) {
    return value * TWH_TO_GWH;
  }
  
  // Convert to TWh
  else if (sourceUnit === MWH_UNIT && targetUnit === TWH_UNIT) {
    return value * MWH_TO_TWH;
  } else if (sourceUnit === GWH_UNIT && targetUnit === TWH_UNIT) {
    return value * GWH_TO_TWH;
  }
  
  // If already in the target unit or no conversion needed
  return value;
}

/**
 * Calculate country summaries from grid and generation data
 * @param data - Raw data object
 * @param shellGenPowerData - Shell generated power data
 * @returns Country summaries or error message
 */
export function calculateCountrySummaries(
  data: RawData, 
  shellGenPowerData: Array<GridRegionDataItem>
): CountrySummaries | string {
  const processedData = technologyWiseData(data);

  try {
    // Group by country
    const countriesData: CountrySummaries = {};
    
    // Process grid region data
    if (Array.isArray(processedData)) {
      processedData.forEach(item => {
        const country = item.country;
        
        if (!countriesData[country]) {
          countriesData[country] = initializeCountryData();
        }
        
        // Add grid region data to country totals
        addRegionToCountryData(countriesData[country], item);
      });
    }
    
    // Add Shell generated power to country totals
    processShellGeneration(shellGenPowerData, countriesData);
    
    return countriesData;
  } catch (error) {
    return error instanceof Error ? error.message : "An unknown error occurred";
  }
}

/**
 * Process the raw data to calculate renewable and non-renewable volumes
 * @param data - The raw data from the database
 * @returns Processed data organized by grid region or error message
 */
export function technologyWiseData(
  data: RawData
): Array<ProcessedGridRegionData> | string {
  try {
    // Constants for sheet names
    const SHEET_TOTAL_3P_GEN_SALES = "Total 3P Gen Sales";
    const SHEET_RENEWABLE_CERTIFICATES = "Renewable Certificates";
    const SHEET_3P_GEN_TECH_SPECIFIC = "3P Gen Tech Specific";
    
    // Safely extract data from the response with fallbacks to empty arrays
    const total3PGenSalesData = data?.[SHEET_TOTAL_3P_GEN_SALES] ?? [];
    const renewableCertificatesData = data?.[SHEET_RENEWABLE_CERTIFICATES] ?? [];
    const threePGenTechSpecificData = data?.[SHEET_3P_GEN_TECH_SPECIFIC] ?? [];
    
    // Get unique grid regions
    const gridRegions = extractUniqueGridRegions(total3PGenSalesData);
    
    // Process each grid region
    return gridRegions.map(gridRegion => 
      processGridRegionData(
        gridRegion, 
        total3PGenSalesData, 
        renewableCertificatesData, 
        threePGenTechSpecificData
      )
    );
  } catch (error) {
    return error instanceof Error 
      ? error.message ?? "Error processing technology-wise data"
      : "Error processing technology-wise data";
  }
}

/**
 * Initialize country data structure
 * @returns Empty country data structure
 */
export function initializeCountryData(): CountryData {
  return {
    totalRenewable: 0,
    totalNonRenewable: 0,
    totalResidualGrid: 0,
    totalR4Q: 0,
    regions: []
  };
}

/**
 * Add grid region data to country data
 * @param countryData - Country data object
 * @param item - Grid region data
 * @returns Updated country data
 */
export function addRegionToCountryData(
  countryData: CountryData, 
  item: ProcessedGridRegionData
): CountryData {
  countryData.totalRenewable += item[REGION_VOLUME_RENEWABLE] ?? 0;
  countryData.totalNonRenewable += item[REGION_VOLUME_NONRENEWABLE] ?? 0;
  countryData.totalResidualGrid += item[REGION_VOLUME_RESIDUALGRID] ?? 0;
  countryData.totalR4Q += item[R4Q_PROPERTY] ?? 0;
  
  // Add region details
  countryData.regions.push({
    [REGION_GRID_REGION]: item[REGION_GRID_REGION],
    [R4Q_PROPERTY]: item[R4Q_PROPERTY] ?? 0,
    [REGION_VOLUME_RENEWABLE]: item[REGION_VOLUME_RENEWABLE] ?? 0,
    [REGION_VOLUME_NONRENEWABLE]: item[REGION_VOLUME_NONRENEWABLE] ?? 0,
    [REGION_VOLUME_RESIDUALGRID]: item[REGION_VOLUME_RESIDUALGRID] ?? 0
  });
  
  return countryData;
}

/**
 * Extract unique grid regions from data
 * @param total3PGenSalesData - The total 3P gen sales data
 * @returns Array of unique grid regions
 */
export function extractUniqueGridRegions(
  total3PGenSalesData: Array<GridRegionDataItem>
): Array<string> {
  if (!Array.isArray(total3PGenSalesData)) {
    return [];
  }
  
  return [...new Set(
    total3PGenSalesData
      .map(item => item[REGION_GRID_PROPERTY])
      .filter((region): region is string => !!region) // Type guard to filter out undefined/null
  )];
}

/**
 * Process grid region data to extract renewable and non-renewable volumes
 * @param gridRegion - Grid region string
 * @param total3PGenSalesData - Total 3P gen sales data
 * @param renewableCertificatesData - Renewable certificates data
 * @param threePGenTechSpecificData - 3P gen tech specific data
 * @returns Processed grid region data
 */
export function processGridRegionData(
  gridRegion: string,
  total3PGenSalesData: Array<GridRegionDataItem>,
  renewableCertificatesData: Array<GridRegionDataItem>,
  threePGenTechSpecificData: Array<GridRegionDataItem>
): ProcessedGridRegionData {
  // Find total sales for this grid region
  const totalSales = total3PGenSalesData.find(item => item[REGION_GRID_PROPERTY] === gridRegion);
  
  // Determine the business segment and units
  const businessSegment = totalSales?.[BUSINESS_SEGMENT_PROPERTY] ?? "";
  const isSE = isSEBusinessSegment(businessSegment);
  const inputUnits = totalSales?.[UNITS_PROPERTY] ?? MWH_UNIT;
  
  // Get R4Q value and convert to appropriate unit if needed
  let r4q = safeParseFloat(String(totalSales?.[R4Q_PROPERTY] ?? ""));
  
  // For SE segments, we want the final output in TWh
  // If input is already in GWh, convert to TWh
  if (isSE && inputUnits === GWH_UNIT) {
    r4q = convertEnergyValue(r4q, GWH_UNIT, TWH_UNIT);
  }
  
  // Calculate renewable volume from certificates and tech-specific data
  const renewableCertificatesVolume = calculateRenewableCertificatesVolume(
    gridRegion, 
    renewableCertificatesData, 
    isSE
  );
  
  const renewableTechVolume = calculateRenewableTechVolume(
    gridRegion, 
    threePGenTechSpecificData, 
    isSE
  );
  
  const renewableVolume = renewableCertificatesVolume + renewableTechVolume;
  
  // Calculate non-renewable volume
  const nonRenewable = calculateNonRenewableVolume(
    gridRegion, 
    threePGenTechSpecificData, 
    isSE
  );
  
  // Calculate residual grid volume
  const residualGrid = r4q - renewableVolume - nonRenewable;
  
  // Extract country from grid region
  const country = extractCountry(gridRegion);
  
  return {
    country,
    [REGION_GRID_REGION]: gridRegion,
    [R4Q_PROPERTY]: r4q,
    [REGION_VOLUME_RENEWABLE]: renewableVolume,
    [REGION_VOLUME_NONRENEWABLE]: nonRenewable,
    [REGION_VOLUME_RESIDUALGRID]: residualGrid
  };
}

/**
 * Extract country from a region|grid string
 * @param regionGrid - The region|grid string
 * @returns The extracted country
 */
function extractCountry(regionGrid: string): string {
  if (!regionGrid) {
    return '';
  }
  
  if (regionGrid.includes('|')) {
    return regionGrid.split('|')[0].trim();
  }
  
  return regionGrid;
}

/**
 * Calculate the volume for a specific grid region from renewable certificates
 * @param gridRegion - Grid region to calculate for
 * @param renewableCertificatesData - Renewable certificates data
 * @param isSE - Whether this is SE business segment
 * @returns Total volume for this grid region
 */
export function calculateRenewableCertificatesVolume(
  gridRegion: string,
  renewableCertificatesData: Array<GridRegionDataItem>,
  isSE: boolean
): number {
  if (!Array.isArray(renewableCertificatesData)) {
    return 0;
  }
  
  return renewableCertificatesData
    .filter(item => item[REGION_GRID_PROPERTY] === gridRegion)
    .reduce((sum, item) => {
      const value = safeParseFloat(String(item[R4Q_PROPERTY] ?? ""));
      const units = item[UNITS_PROPERTY] ?? MWH_UNIT;
      
      // Convert to TWh for SE business segments
      if (isSE) {
        return sum + convertEnergyValue(value, units, TWH_UNIT);
      }
      
      // For non-SE, keep in GWh
      return sum + convertEnergyValue(value, units, GWH_UNIT);
    }, 0);
}

/**
 * Calculate the renewable volume for a specific grid region
 * @param gridRegion - Grid region to calculate for
 * @param threePGenTechSpecificData - 3P gen tech specific data
 * @param isSE - Whether this is SE business segment
 * @returns Total renewable volume for this grid region
 */
export function calculateRenewableTechVolume(
  gridRegion: string,
  threePGenTechSpecificData: Array<GridRegionDataItem>,
  isSE: boolean
): number {
  if (!Array.isArray(threePGenTechSpecificData)) {
    return 0;
  }
  
  // Filter items first
  const filteredItems = threePGenTechSpecificData.filter(item => 
    item[REGION_GRID_PROPERTY] === gridRegion && 
    isRenewableTechnology(item[TECHNOLOGY_PROPERTY] ?? '')
  );
  
  // Calculate sum with unit conversion
  return filteredItems.reduce((acc, item) => {
    const value = safeParseFloat(String(item[R4Q_PROPERTY] ?? ""));
    const units = item[UNITS_PROPERTY] ?? MWH_UNIT;
    
    // Convert to TWh for SE business segments
    if (isSE) {
      return acc + convertEnergyValue(value, units, TWH_UNIT);
    }
    
    // For non-SE, keep in GWh
    return acc + convertEnergyValue(value, units, GWH_UNIT);
  }, 0);
}

/**
 * Calculate the non-renewable volume for a specific grid region
 * @param gridRegion - Grid region to calculate for
 * @param threePGenTechSpecificData - 3P gen tech specific data
 * @param isSE - Whether this is SE business segment
 * @returns Total non-renewable volume for this grid region
 */
export function calculateNonRenewableVolume(
  gridRegion: string,
  threePGenTechSpecificData: Array<GridRegionDataItem>,
  isSE: boolean
): number {
  if (!Array.isArray(threePGenTechSpecificData)) {
    return 0;
  }
  
  return threePGenTechSpecificData
    .filter(item => 
      item[REGION_GRID_PROPERTY] === gridRegion && 
      !isRenewableTechnology(item[TECHNOLOGY_PROPERTY] ?? '')
    )
    .reduce((sum, item) => {
      const value = safeParseFloat(String(item[R4Q_PROPERTY] ?? ""));
      const units = item[UNITS_PROPERTY] ?? MWH_UNIT;
      
      // Convert to TWh for SE business segments
      if (isSE) {
        return sum + convertEnergyValue(value, units, TWH_UNIT);
      }
      
      // For non-SE, keep in GWh
      return sum + convertEnergyValue(value, units, GWH_UNIT);
    }, 0);
}

/**
 * Process Shell's generation power data 
 * @param shellGenPowerData - Shell generation power data
 * @param countriesData - Countries data to update
 * @returns Updated countries data
 */
export function processShellGeneration(
  shellGenPowerData: Array<GridRegionDataItem>,
  countriesData: CountrySummaries
): CountrySummaries {
  if (!Array.isArray(shellGenPowerData)) {
    return countriesData;
  }
  
  shellGenPowerData.forEach(item => {
    const country = item[COUNTRY_PROPERTY];
    if (!country) {
      return; // Skip items without a country
    }
    
    // Check for dash values in a safer way
    const r4qStr = String(item[R4Q_PROPERTY] ?? "");
    if (r4qStr.includes('-') || r4qStr.trim() === '') {
      return; // Skip items with dash values or empty strings
    }
    
    if (!countriesData[country]) {
      countriesData[country] = initializeCountryData();
    }
    
    const technology = item[TECHNOLOGY_PROPERTY] ?? '';
    const isRenewable = isRenewableTechnology(technology);
    const businessSegment = item[BUSINESS_SEGMENT_PROPERTY] ?? "";
    const isSE = isSEBusinessSegment(businessSegment);
    const units = item[UNITS_PROPERTY] ?? GWH_UNIT;
    
    // Parse and convert the value based on business segment
    let r4q = safeParseFloat(r4qStr);
    
    // Apply unit conversion
    if (isSE) {
      r4q = convertEnergyValue(r4q, units, TWH_UNIT);
    } else {
      // For non-SE, ensure the value is in GWh
      r4q = convertEnergyValue(r4q, units, GWH_UNIT);
    }
    
    // Add to the appropriate total
    if (isRenewable) {
      countriesData[country].totalRenewable += r4q;
    } else {
      countriesData[country].totalNonRenewable += r4q;
    }
    
    countriesData[country].totalR4Q += r4q;
  });
  
  return countriesData;
}